import type { Person } from "@b2bportal/air-booking-api";
import type { ProductType } from "@b2bportal/core-types";
import type { CipherText } from "@b2bportal/purchase-api";
import type { Traveler } from "@b2bportal/traveler-api";
import type {
  ContactEvents,
  FlightPassengerEvents,
  CartQuoteEvents,
  SeatEvents,
  VipSupportEvents,
  CardPaymentEvents,
  CartFulfillEvents,
  CfarEventsV2,
  DisruptionEvents,
} from "@hopper-b2b/checkout";
import type { PersonId, Payment } from "@hopper-b2b/types";
import type { AuthEvents } from "@hsbc/auth";
import type { RewardsPaymentEvents } from "@hsbc/rewards";
import type { WalletPaymentEvent } from "@hsbc/wallet";

export enum Event {
  // CommonEvents
  NEXT = "NEXT",
  PREVIOUS = "PREVIOUS",
  CHANGE = "CHANGE",
  GO_IDLE = "GO_IDLE",
  RESET_CONTEXT = "RESET_CONTEXT",

  // Traveler Events
  DELETE_TRAVELER = "DELETE_TRAVELER",
  SET_CURRENT_TRAVELER = "SET_CURRENT_TRAVELER",
  CLEAR_TRAVELER_INFORMATION_ERROR = "CLEAR_TRAVELER_INFORMATION_ERROR",
  OPEN_TRAVELER_PICKER = "OPEN_TRAVELER_PICKER",
  OPEN_TRAVELER_FORM = "OPEN_TRAVELER_FORM",
  OPEN_TRAVELER_DELETE_MODAL = "OPEN_TRAVELER_DELETE_MODAL",
  ADD_TRAVELER = "ADD_TRAVELER",
  COMPLETE = "COMPLETE",
  SELECT_TRAVELER = "SELECT_TRAVELER",
  UPDATE_TRAVELER = "UPDATE_TRAVELER",
  VALIDATE_TRAVELER = "VALIDATE_TRAVELER",
  OPEN_PASSPORT_FORM = "OPEN_PASSPORT_FORM",
  ON_INFANT_MODAL_CONTINUE = "ON_INFANT_MODAL_CONTINUE",
  SET_CURRENT_INFANT_ID = "SET_CURRENT_INFANT_ID",

  OPEN_FORM_AND_SET_PASSENGER = "OPEN_FORM_AND_SET_PASSENGER",

  // ContactInformationEvents
  SET_CONTACT_INFO = "SET_CONTACT_INFO",
  CLEAR_CONTACT_ERROR = "CLEAR_CONTACT_ERROR",

  // PassportEvents
  UPDATE_PASSPORT_AND_CONTINUE = "UPDATE_PASSPORT_AND_CONTINUE",
  VALIDATE_PASSENGERS = "VALIDATE_PASSENGERS",
  CLEAR_PASSPORT_ERROR = "CLEAR_PASSPORT_ERROR",

  // ScheduleQuote
  CLEAR_SCHEDULE_QUOTE_ERROR = "CLEAR_SCHEDULE_QUOTE_ERROR",

  // PaymentInformation
  OPEN_PAYMENT_FORM = "OPEN_PAYMENT_FORM",
  CLOSE_PAYMENT_FORM = "CLOSE_PAYMENT_FORM",
  SET_APPLIED_BALANCE = "SET_APPLIED_BALANCE",
  VERIFY_PAYMENT_METHOD = "VERIFY_PAYMENT_METHOD",
  DELETE_PAYMENT_METHOD = "DELETE_PAYMENT_METHOD",
  SET_PAYMENT_METHOD_ID = "SET_PAYMENT_METHOD_ID",
  SET_SELECTED_PAYMENT_METHOD_ID = "SET_SELECTED_PAYMENT_METHOD_ID",
  CLEAR_PAYMENT_ERROR = "CLEAR_PAYMENT_ERROR",

  // UBER Payment
  SET_PAYMENT_DATA_AND_GO_NEXT = "SET_PAYMENT_DATA_AND_GO_NEXT",

  // Review
  GO_TO_PASSENGER_SELECT = "GO_TO_PASSENGER_SELECT",
  GO_TO_CONTACT_INFORMATION = "GO_TO_CONTACT_INFORMATION",

  // ScheduleFulfill
  CLEAR_SCHEDULE_FULFILL_ERROR = "CLEAR_SCHEDULE_FULFILL_ERROR",

  // Confirmation
  GO_HOME = "GO_HOME",

  // Transition to state from query params
  GO_TO_CART_QUOTE = "GO_TO_CART_QUOTE",
  GO_TO_SEAT_SELECTION = "GO_TO_SEAT_SELECTION",
  GO_TO_CARD_PAYMENT = "GO_TO_CARD_PAYMENT",
  GO_TO_REVIEW = "GO_TO_REVIEW",
  GO_TO_BOOKING_CONFIRMATION = "GO_TO_BOOKING_CONFIRMATION",
  GO_TO_UPDATE_BEFORE_FULFILL = "GO_TO_UPDATE_BEFORE_FULFILL",

  // Price Drop
  SET_CANDIDATE_ID = "SET_CANDIDATE_ID",
}

export const CheckoutEvent = {
  ...Event,
} as const;

export type CheckoutEvent = (typeof CheckoutEvent)[keyof typeof CheckoutEvent];

export interface NextEvent {
  type: Event.NEXT;
  productType?: ProductType;
}
export interface PreviousEvent {
  type: Event.PREVIOUS;
}

export interface SelectTravelerEvent {
  type: Event.SELECT_TRAVELER;
  singleTravelerWorkflow?: boolean;
  travelerId?: PersonId;
  data?: Person;
}

export interface UpdateTraveler {
  type: Event.UPDATE_TRAVELER;
  traveler: Traveler;
}

export interface SetCurrentTravelerEvent {
  type: Event.SET_CURRENT_TRAVELER;
  traveler: Traveler;
}

export interface ClearTravelerInformationError {
  type: Event.CLEAR_TRAVELER_INFORMATION_ERROR;
}

export type OpenTravelerForm = {
  type: Event.OPEN_TRAVELER_FORM;
};

export type OpenTravelerPicker = {
  type: Event.OPEN_TRAVELER_PICKER;
};

export type OpenPassportForm = {
  type: Event.OPEN_PASSPORT_FORM;
};

export type DeleteTraveler = {
  type: Event.DELETE_TRAVELER;
  travelerId: string;
};

export interface OpenPassengerFormAndSetCurrentPassenger {
  type: Event.OPEN_FORM_AND_SET_PASSENGER;
  passenger: Traveler;
}

export interface GoIdleEvent {
  type: Event.GO_IDLE;
}

export interface OpenPassportFormEvent {
  type: Event.OPEN_PASSPORT_FORM;
}

export interface GoIdleEvent {
  type: Event.GO_IDLE;
}

export interface OnInfantModalContinue {
  type: Event.ON_INFANT_MODAL_CONTINUE;
  seatType: "OwnSeat" | "OnLap";
}

export interface OpenPaymentFormEvent {
  type: Event.OPEN_PAYMENT_FORM;
}

export interface ClosePaymentFormEvent {
  type: Event.CLOSE_PAYMENT_FORM;
}

export interface SetAppliedBalanceEvent {
  type: Event.SET_APPLIED_BALANCE;
  appliedBalance: number;
}

export interface VerifyPaymentMethodEvent {
  type: Event.VERIFY_PAYMENT_METHOD;
  token: string;
}

export interface DeletePaymentMethodEvent {
  type: Event.DELETE_PAYMENT_METHOD;
  paymentMethod: {
    paymentId: string;
  };
}

export interface SetPaymentMethodIdEvent {
  type: Event.SET_PAYMENT_METHOD_ID;
  data: {
    selectedPaymentMethodId: number | string;
    paymentMethods: Payment[];
  };
}

export interface SetSelectedPaymentMethodIdEvent {
  type: Event.SET_SELECTED_PAYMENT_METHOD_ID;
  paymentMethod: {
    paymentId: string | undefined;
  };
}

export interface SetPaymentDataAndGoNext {
  type: Event.SET_PAYMENT_DATA_AND_GO_NEXT;
  paymentQuoteToken: CipherText;
  paymentSessionToken: string;
}

export interface ClearScheduleQuoteError {
  type: Event.CLEAR_SCHEDULE_QUOTE_ERROR;
}

export interface ClearScheduleFulfillError {
  type: Event.CLEAR_SCHEDULE_FULFILL_ERROR;
}

export interface GoToPassengerSelect {
  type: Event.GO_TO_PASSENGER_SELECT;
}

export interface GoToContactInformation {
  type: Event.GO_TO_CONTACT_INFORMATION;
}

export interface GoHomeEvent {
  type: Event.GO_HOME;
}

export interface UpdatePassportAndContinue {
  type: Event.UPDATE_PASSPORT_AND_CONTINUE;
  traveler: Traveler;
}

export interface ValidatePassengers {
  type: Event.VALIDATE_PASSENGERS;
}

export interface ClearPassportError {
  type: Event.CLEAR_PASSPORT_ERROR;
}

export interface ResetContextEvent {
  type: Event.RESET_CONTEXT;
}

export interface GoToCardPayment {
  type: Event.GO_TO_CARD_PAYMENT;
}
export interface GoToReview {
  type: Event.GO_TO_REVIEW;
}

export interface GoToSeatSelection {
  type: Event.GO_TO_SEAT_SELECTION;
}

export interface GoToBookingConfirmation {
  type: Event.GO_TO_BOOKING_CONFIRMATION;
}

export interface GoToCartQuote {
  type: Event.GO_TO_CART_QUOTE;
}

export interface ClearContactError {
  type: Event.CLEAR_CONTACT_ERROR;
}

export interface ClearPaymentError {
  type: Event.CLEAR_PAYMENT_ERROR;
}

export interface SetCandidateIdEvent {
  type: Event.SET_CANDIDATE_ID;
  candidateId: string;
}

export type TEvent =
  | ClearScheduleFulfillError
  | ClearScheduleQuoteError
  | ClearPaymentError
  | GoHomeEvent
  | GoIdleEvent
  | GoToContactInformation
  | GoToPassengerSelect
  | NextEvent
  | OnInfantModalContinue
  | OpenPassportFormEvent
  | PreviousEvent
  | OpenPaymentFormEvent
  | ClosePaymentFormEvent
  | SetAppliedBalanceEvent
  | VerifyPaymentMethodEvent
  | DeletePaymentMethodEvent
  | SetPaymentMethodIdEvent
  | SetSelectedPaymentMethodIdEvent
  | UpdatePassportAndContinue
  | ValidatePassengers
  | ClearPassportError
  | ResetContextEvent
  | ClearContactError
  | SetPaymentDataAndGoNext
  | GoToCardPayment
  | GoToReview
  | GoToSeatSelection
  | GoToBookingConfirmation
  | GoToCartQuote
  // Traveler Events
  | ClearTravelerInformationError
  | OpenTravelerForm
  | OpenTravelerPicker
  | OpenPassportForm
  | DeleteTraveler
  | OpenPassengerFormAndSetCurrentPassenger
  | SelectTravelerEvent
  | SetCurrentTravelerEvent
  | UpdateTraveler
  // Price Drop Events
  | SetCandidateIdEvent
  // Auth Events
  | AuthEvents
  // Contact Information Events
  | ContactEvents
  // Passenger Information Events
  | FlightPassengerEvents
  // Cart Quote Events
  | CartQuoteEvents
  // Cart Fulfill Events
  | CartFulfillEvents
  // Seat Selection Events
  | SeatEvents
  // Vip Support Events
  | VipSupportEvents
  // Card Payment Events
  | CardPaymentEvents
  // Rewards Events
  | RewardsPaymentEvents
  // Cfar Events
  | CfarEventsV2
  // Disruption Events
  | DisruptionEvents
  // Wallet Events
  | WalletPaymentEvent;
